import { ConnectionPositionPair, OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import {
  Component,
  TemplateRef,
  computed,
  forwardRef,
  input,
  model,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { defaultDropdownPositionPairs } from '../overlay/cdk-overlay-position-pairs';

@Component({
  selector: 'pozi-dropdown',
  host: {
    '[attr.disabled]': 'disabled() ? "" : null',
  },
  imports: [CommonModule, OverlayModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => DropdownComponent),
    },
  ],
  templateUrl: './dropdown.component.html',
  styleUrls: ['./dropdown.component.scss'],
})
export class DropdownComponent<T> implements ControlValueAccessor {
  disabled = model<boolean>(false);

  items = input<T[]>([]);
  icon = input<string>('');
  itemGroup = input<string>('');
  showTitle = input<boolean>(true);
  template = input<TemplateRef<undefined> | null>(null);
  dropdownOpen = signal(false);
  placeholder = input<string | null>(null);
  isInputStyle = input<boolean>(false);
  isInvalidStyle = input<boolean>(false);
  /**
   * Marker class put on the CDK pane **in addition to** `cdk-dropdown`, so a
   * consumer can key its own `::ng-deep .cdk-overlay-pane…` rules on the panes
   * this dropdown opens for it. The pane is created by the CDK under `<body>`
   * and carries no `_ngcontent` attribute, so a class on the pane is the only
   * scoping seam a consumer's sheet has. `cdk-dropdown` is kept because it
   * carries the shared pane theming in `styles.scss`.
   */
  panelClass = input<string>();
  cdkOverlayPositionPairs: ConnectionPositionPair[] =
    defaultDropdownPositionPairs;

  readonly overlayPanelClasses = computed<string[]>(() => {
    const extra = this.panelClass();
    return extra ? ['cdk-dropdown', extra] : ['cdk-dropdown'];
  });

  touched = false;
  /** Signal: template-read and written from `writeValue`, outside change detection. */
  readonly selectedItem = signal<T | undefined>(undefined);
  onChange: (model: T) => void = () => {};
  onTouched: () => void = () => {};

  selectItem(item: T) {
    if (!this.touched && this.selectedItem() !== item) {
      this.onTouched();
      this.touched = true;
    }

    this.selectedItem.set(item);
    this.onChange(item);
    this.dropdownOpen.set(false);
  }

  toggleDropdown() {
    this.dropdownOpen.update((o) => !o);
  }

  registerOnChange(fn: (model: T) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  writeValue(model: T): void {
    this.selectedItem.set(model);
  }
}
